import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { getChannelTemplateAthletes } from '@/services/channelApi';
import type { PerTemplateAthleteResponse } from '@/types/channel';

/**
 * Fetches per-template athlete summary stats for a given channel + template.
 *
 * Both params are optional so callers can derive them lazily (e.g. from a
 * click handler that sets state). The query is disabled when either is absent.
 *
 * 403 responses (non-owner access) are surfaced as an error so the caller can
 * display a friendly "access denied" message — the backend enforces the gate.
 */
export function useChannelTemplateAthletes(
  slug?: string,
  templateId?: string,
) {
  return useQuery<PerTemplateAthleteResponse>({
    queryKey: ['channel-template-athletes', slug, templateId],
    enabled: Boolean(slug && templateId),
    queryFn: async () => {
      try {
        return await getChannelTemplateAthletes(slug!, templateId!);
      } catch (err) {
        // Let 404 / 403 surface as errors so the component can render appropriate states
        if (isAxiosError(err) && err.response?.status === 404) {
          // Endpoint not deployed yet — return an empty result rather than crashing
          return {
            channel_slug: slug!,
            template_id: templateId!,
            athletes: [],
            total_athletes: 0,
          };
        }
        throw err;
      }
    },
  });
}
